'use client';
import React, { useEffect, useRef } from 'react';
import Image from 'next/image';
import Icon from '@/components/ui/AppIcon';

interface FaqItem {
  question: string;
  answer: string | React.ReactNode;
  imageSrc: string;
  imageAlt: string;
  tag: string;
  microCta: string;
}

interface FaqZigzagProps {
  item: FaqItem;
  index: number;
  onCta: () => void;
}

export default function FaqZigzag({ item, index, onCta }: FaqZigzagProps) {
  const sectionRef = useRef<HTMLDivElement>(null);
  const isEven = index % 2 === 0;

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    el.classList.add('hidden-initial');
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.classList.remove('hidden-initial');
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={sectionRef} className="reveal py-14 md:py-20">
      <div
        className={`flex flex-col ${isEven ? 'md:flex-row' : 'md:flex-row-reverse'} gap-8 md:gap-16 items-center`}
      >
        {/* Question side */}
        <div className="w-full md:w-1/2 flex flex-col gap-5">
          <div className="flex items-center gap-3">
            <div
              className="pulse-dot w-2 h-2 rounded-full"
              style={{ background: '#2D5F9A' }}
            />
            <span className="mono text-[10px] font-semibold tracking-widest uppercase" style={{ color: '#2D5F9A' }}>
              {item.tag}
            </span>
          </div>
          <h3
            className="text-2xl md:text-3xl font-bold leading-tight tracking-tight"
            style={{ color: '#F5F5F7' }}
          >
            {item.question}
          </h3>
          <div className="text-sm md:text-base leading-relaxed" style={{ color: 'rgba(168,169,173,0.8)' }}>
            {item.answer}
          </div>
          <button
            onClick={onCta}
            className="inline-flex items-center gap-2 self-start btn-sapphire px-6 py-3 rounded-full mono text-[11px] font-semibold tracking-widest uppercase text-white"
          >
            {item.microCta}
            <Icon name="ArrowRightIcon" size={14} variant="outline" />
          </button>
        </div>

        {/* Image side */}
        <div className="w-full md:w-1/2">
          <div
            className="relative overflow-hidden rounded-2xl"
            style={{ border: '1px solid rgba(212,212,216,0.1)', aspectRatio: '4/3' }}
          >
            <Image
              src={item.imageSrc}
              alt={item.imageAlt}
              fill
              className="object-cover"
              sizes="(max-width: 768px) 100vw, 50vw"
            />
            <div
              className="absolute inset-0"
              style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.3) 0%, transparent 60%)' }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}